import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GenericCrudPage from "./GenericCrudPage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { Stethoscope, CheckCircle2, XCircle, Clock, Users, Plus, Pencil, Trash2, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";

// ---------- PKS Vendor Paramedic (unchanged behavior) ----------
const STATUS_PKS = ["ACTIVE","EXPIRING_SOON","EXPIRED"];
const pksBadge = (s) => {
  const m = { ACTIVE:"bg-emerald-100 text-emerald-700", EXPIRING_SOON:"bg-amber-100 text-amber-700", EXPIRED:"bg-red-100 text-red-700" };
  const l = { ACTIVE:"AKTIF", EXPIRING_SOON:"AKAN BERAKHIR", EXPIRED:"BERAKHIR" };
  return <Badge className={`${m[s] || m.EXPIRED}`}>{l[s] || s}</Badge>;
};

export function ParamedicVendorPage() {
  return <GenericCrudPage resource="paramedic-vendors" title="PKS Vendor Paramedic" subtitle="Pre-Flight Medical"
    moduleKey="preflight" testidPrefix="paramedic-vendors"
    fields={[
      { key: "vendor_name", label: "Vendor", colSpan: 2 },
      { key: "agreement_number", label: "Nomor PKS", mono: true },
      { key: "pic", label: "PIC" },
      { key: "contact", label: "Kontak" },
      { key: "base", label: "Base", type: "select", options: ["CGK","DPS","SUB","MES","BPN","UPG"] },
      { key: "start_date", label: "Mulai", type: "date" },
      { key: "end_date", label: "Berakhir", type: "date" },
      { key: "status", label: "Status", type: "select", options: STATUS_PKS, badge: pksBadge },
      { key: "attachment", label: "Dokumen PKS", type: "file", colSpan: 2, hideInTable: true },
      { key: "notes", label: "Catatan", type: "textarea", colSpan: 2, hideInTable: true },
    ]}
    defaultItem={{ vendor_name:"", agreement_number:"", pic:"", contact:"", base:"CGK", start_date:"", end_date:"", status:"ACTIVE", notes:"", attachment:null }}
    searchKeys={["vendor_name","agreement_number","pic","base"]}
    filterFields={[{ key: "status", label: "Status", options: STATUS_PKS }, { key: "base", label: "Base", options: ["CGK","DPS","SUB","MES","BPN","UPG"] }]}
    summaryCards={[
      { label: "Total Vendor", icon: Users, tone: "bg-slate-100 text-slate-700", compute:(d)=>d.length },
      { label: "Aktif", icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700", compute:(d)=>d.filter(x=>x.status==="ACTIVE").length },
      { label: "Akan Berakhir", icon: Clock, tone: "bg-amber-100 text-amber-700", compute:(d)=>d.filter(x=>x.status==="EXPIRING_SOON").length },
      { label: "Berakhir", icon: XCircle, tone: "bg-red-100 text-red-700", compute:(d)=>d.filter(x=>x.status==="EXPIRED").length },
    ]}
  />;
}

// ---------- Pre-Flight Medical Report ----------
const RANKS = ["Capt","FO","FA1","FA"];
const CATEGORIES = ["Company Limit","Optimum","Normal","High Normal","Hypertension","No check"];
const NOTES = ["FIT","UNFIT"];
const ALCOHOL = ["PASS","FAIL","NO CHECK"];
const MONTHS = [
  { v:"01", l:"Januari" }, { v:"02", l:"Februari" }, { v:"03", l:"Maret" },
  { v:"04", l:"April" },   { v:"05", l:"Mei" },      { v:"06", l:"Juni" },
  { v:"07", l:"Juli" },    { v:"08", l:"Agustus" },  { v:"09", l:"September" },
  { v:"10", l:"Oktober" }, { v:"11", l:"November" }, { v:"12", l:"Desember" },
];
const BASE_RE = /^[A-Z]{3}$/;

const rankBadge = (r) => <Badge variant="outline" className="text-[10px] font-semibold">{r || "—"}</Badge>;
const noteBadge = (n) => n === "FIT" ? <Badge className="bg-emerald-100 text-emerald-700">FIT</Badge> : n === "UNFIT" ? <Badge className="bg-red-100 text-red-700">UNFIT</Badge> : <Badge className="bg-slate-100 text-slate-700">—</Badge>;
const alcoBadge = (a) => a === "PASS" ? <Badge className="bg-emerald-100 text-emerald-700">PASS</Badge> : a === "FAIL" ? <Badge className="bg-red-100 text-red-700">FAIL</Badge> : <Badge className="bg-slate-100 text-slate-600">NO CHECK</Badge>;
const catBadge = (c) => {
  const map = {"Company Limit":"bg-violet-100 text-violet-700","Optimum":"bg-emerald-100 text-emerald-700","Normal":"bg-sky-100 text-sky-700","High Normal":"bg-amber-100 text-amber-700","Hypertension":"bg-red-100 text-red-700","No check":"bg-slate-100 text-slate-600"};
  return <Badge className={map[c] || "bg-slate-100 text-slate-700"}>{c || "—"}</Badge>;
};

const EMPTY = { no_id:"", nama:"", rank:"", flight_number:"", time:"", alcohol_test:"", sistole:"", diastole:"", temperature:"", category:"", note:"", remark:"", base:"", date: new Date().toISOString().slice(0,10) };

function ReportFormDialog({ open, onOpenChange, initial, onSave }) {
  const [form, setForm] = React.useState(initial || EMPTY);
  const [errors, setErrors] = React.useState({});
  React.useEffect(() => { setForm(initial || EMPTY); setErrors({}); }, [initial, open]);

  const required = ["no_id","nama","rank","flight_number","time","alcohol_test","sistole","diastole","temperature","category","note","base","date"];
  const validate = () => {
    const e = {};
    required.forEach(k => { if (form[k] === "" || form[k] === null || form[k] === undefined) e[k] = "Wajib diisi"; });
    const b = String(form.base || "").trim().toUpperCase();
    if (b && !BASE_RE.test(b)) e.base = "Base wajib terdiri dari 3 huruf kapital, contoh: CGK.";
    setErrors(e); return Object.keys(e).length === 0;
  };
  const submit = () => {
    if (!validate()) { toast.error("Mohon lengkapi semua field wajib"); return; }
    const body = {
      ...form,
      base: String(form.base || "").trim().toUpperCase(),
      sistole: Number(form.sistole),
      diastole: Number(form.diastole),
      temperature: Number(form.temperature),
    };
    onSave(body);
  };

  const Field = ({ k, label, children, span = 1 }) => (
    <div className={span === 2 ? "col-span-2" : ""}>
      <Label className="text-xs font-semibold">{label} {required.includes(k) && <span className="text-red-500">*</span>}</Label>
      {children}
      {errors[k] && <div className="text-[10px] text-red-600 mt-1">{errors[k]}</div>}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{form?.id ? "Ubah" : "Tambah"} Pre-Flight Medical Report</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field k="no_id" label="No ID"><Input value={form.no_id} onChange={(e)=>setForm({...form, no_id:e.target.value})} data-testid="pf-no-id"/></Field>
          <Field k="nama" label="Nama" span={2}><Input value={form.nama} onChange={(e)=>setForm({...form, nama:e.target.value})} data-testid="pf-nama"/></Field>
          <Field k="rank" label="Rank">
            <Select value={form.rank} onValueChange={(v)=>setForm({...form, rank:v})}>
              <SelectTrigger data-testid="pf-rank"><SelectValue placeholder="Pilih Rank"/></SelectTrigger>
              <SelectContent>{RANKS.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field k="flight_number" label="Flight Number"><Input value={form.flight_number} onChange={(e)=>setForm({...form, flight_number:e.target.value})} placeholder="mis. QG123"/></Field>
          <Field k="base" label="Base">
            <Input
              value={form.base || ""}
              maxLength={3}
              placeholder="Contoh: CGK"
              className="uppercase tracking-widest font-mono"
              onChange={(e)=>{
                const cleaned = e.target.value.toUpperCase().replace(/[^A-Z]/g,'').slice(0,3);
                setForm({...form, base: cleaned});
              }}
              data-testid="pf-base"
            />
          </Field>
          <Field k="time" label="Time"><Input type="time" value={form.time} onChange={(e)=>setForm({...form, time:e.target.value})}/></Field>
          <Field k="date" label="Tanggal Pemeriksaan">
            <Input type="date" value={form.date || ""} onChange={(e)=>setForm({...form, date:e.target.value})} data-testid="pf-date"/>
          </Field>
          <div/>
          <Field k="alcohol_test" label="Alcohol Test">
            <Select value={form.alcohol_test} onValueChange={(v)=>setForm({...form, alcohol_test:v})}>
              <SelectTrigger data-testid="pf-alcohol"><SelectValue placeholder="Pilih"/></SelectTrigger>
              <SelectContent>{ALCOHOL.map(a=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field k="sistole" label="Sistole (mmHg)"><Input type="number" value={form.sistole} onChange={(e)=>setForm({...form, sistole:e.target.value})}/></Field>
          <Field k="diastole" label="Diastole (mmHg)"><Input type="number" value={form.diastole} onChange={(e)=>setForm({...form, diastole:e.target.value})}/></Field>
          <Field k="temperature" label="Temperature (°C)"><Input type="number" step="0.1" value={form.temperature} onChange={(e)=>setForm({...form, temperature:e.target.value})}/></Field>
          <Field k="category" label="Category">
            <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
              <SelectTrigger data-testid="pf-category"><SelectValue placeholder="Pilih Category"/></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field k="note" label="Note">
            <Select value={form.note} onValueChange={(v)=>setForm({...form, note:v})}>
              <SelectTrigger data-testid="pf-note"><SelectValue placeholder="Pilih Note"/></SelectTrigger>
              <SelectContent>{NOTES.map(n=><SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="col-span-2 md:col-span-3"><Label className="text-xs font-semibold">Remark <span className="text-slate-400 font-normal">(opsional)</span></Label>
            <Textarea rows={2} value={form.remark || ""} onChange={(e)=>setForm({...form, remark:e.target.value})}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Batal</Button>
          <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={submit} data-testid="pf-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LaporanPreflightPage() {
  const qc = useQueryClient();
  const { canWrite } = useAuth();
  const canEdit = canWrite("preflight");

  const [filters, setFilters] = useState({ q:"", base:"ALL", rank:"ALL", category:"ALL", note:"ALL", alco:"ALL", month:"ALL", year:"ALL", remark:"" });
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [page, setPage] = useState(1);
  const PER = 15;

  const { data = [], isLoading } = useQuery({ queryKey:["preflight-reports"], queryFn: async () => (await apiClient.get("/preflight-reports")).data });

  // Dynamic option lists derived from actual DB records
  const years = useMemo(()=>Array.from(new Set(data.map(d=>(d.date||"").slice(0,4)).filter(y=>/^\d{4}$/.test(y)))).sort(), [data]);
  const bases = useMemo(()=>Array.from(new Set(data.map(d=>(d.base||"").toUpperCase()).filter(b=>/^[A-Z]{3}$/.test(b)))).sort(), [data]);

  const filtered = useMemo(()=>data.filter(d => {
    const dt = d.date || "";
    const dYear = dt.slice(0,4);
    const dMonth = dt.slice(5,7);
    return (!filters.q || [d.no_id, d.nama, d.flight_number].some(v => (v||"").toLowerCase().includes(filters.q.toLowerCase())))
      && (filters.base==="ALL" || (d.base||"").toUpperCase()===filters.base)
      && (filters.rank==="ALL"||d.rank===filters.rank)
      && (filters.category==="ALL"||d.category===filters.category)
      && (filters.note==="ALL"||d.note===filters.note)
      && (filters.alco==="ALL"||d.alcohol_test===filters.alco)
      && (filters.month==="ALL" || dMonth===filters.month)
      && (filters.year==="ALL" || dYear===filters.year)
      && (!filters.remark || (d.remark||"").toLowerCase().includes(filters.remark.toLowerCase()));
  }), [data, filters]);

  const kpi = useMemo(() => {
    const total = filtered.length;
    const fit = filtered.filter(d=>d.note==="FIT").length;
    const unfit = filtered.filter(d=>d.note==="UNFIT").length;
    const pass = filtered.filter(d=>d.alcohol_test==="PASS").length;
    const fail = filtered.filter(d=>d.alcohol_test==="FAIL").length;
    const nocheck = filtered.filter(d=>d.alcohol_test==="NO CHECK" || !d.alcohol_test).length;
    const pilot = filtered.filter(d=>d.rank==="Capt"||d.rank==="FO").length;
    const fa = filtered.filter(d=>d.rank==="FA"||d.rank==="FA1").length;
    return { total, fit, unfit, pass, fail, nocheck, pilot, fa };
  }, [filtered]);

  const chartFit = [{ name:"FIT", value:kpi.fit },{ name:"UNFIT", value:kpi.unfit }];
  const chartRank = RANKS.map(r => ({ name:r, count: filtered.filter(d=>d.rank===r).length }));
  const chartCat = CATEGORIES.map(c => ({ name:c, count: filtered.filter(d=>d.category===c).length }));
  const chartAlco = [
    { name:"PASS", value:kpi.pass },{ name:"FAIL", value:kpi.fail },{ name:"NO CHECK", value:kpi.nocheck },
  ];
  const chartBase = useMemo(()=>{
    const m = {}; filtered.forEach(d => { const b = d.base || "?"; m[b] = (m[b] || 0) + 1; });
    return Object.entries(m).map(([name, count]) => ({ name, count })).sort((a,b)=>b.count-a.count);
  }, [filtered]);
  const chartTrend = useMemo(()=>{
    const m = {}; filtered.forEach(d => { const day = (d.date || "").slice(0,10); if(!day) return; m[day] = (m[day] || 0) + 1; });
    return Object.entries(m).map(([date, count]) => ({ date, count })).sort((a,b)=>a.date.localeCompare(b.date));
  }, [filtered]);
  const COLORS_FIT = ["#10B981","#EF4444"];
  const COLORS_ALCO = ["#10B981","#EF4444","#94a3b8"];
  const COLORS_CAT = ["#8b5cf6","#10B981","#0ea5e9","#f59e0b","#ef4444","#94a3b8"];

  const mSave = useMutation({
    mutationFn: async (b) => b.id ? (await apiClient.put(`/preflight-reports/${b.id}`, b)).data : (await apiClient.post("/preflight-reports", b)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["preflight-reports"]}); toast.success("Laporan disimpan"); setOpenForm(false); setEditing(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal menyimpan"),
  });
  const mDel = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/preflight-reports/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["preflight-reports"]}); toast.success("Laporan dihapus"); },
  });

  const openNew = () => { setEditing({ ...EMPTY }); setOpenForm(true); };
  const openEdit = (r) => { setEditing({ ...r }); setOpenForm(true); };
  const resetFilters = () => { setFilters({ q:"", base:"ALL", rank:"ALL", category:"ALL", note:"ALL", alco:"ALL", month:"ALL", year:"ALL", remark:"" }); setPage(1); };

  const paged = filtered.slice((page-1)*PER, page*PER);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER));

  const exportCols = [
    { key:"no_id", label:"No ID" },{ key:"nama", label:"Nama" },{ key:"rank", label:"Rank" },
    { key:"flight_number", label:"Flight Number" },{ key:"date", label:"Tanggal" },{ key:"time", label:"Time" },
    { key:"alcohol_test", label:"Alcohol Test" },{ key:"sistole", label:"Sistole" },{ key:"diastole", label:"Diastole" },
    { key:"temperature", label:"Temperature" },{ key:"category", label:"Category" },{ key:"note", label:"Note" },
    { key:"remark", label:"Remark" },{ key:"base", label:"Base" },
  ];

  return (
    <div className="space-y-6" data-testid="laporan-preflight-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#00A651]">Pre-Flight Medical</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Pre-Flight Medical Report</h1>
        <p className="text-sm text-slate-500 mt-1">Ringkasan operasional pemeriksaan kesehatan pra-terbang kru.</p>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          ["Total Check", kpi.total, "bg-slate-100 text-slate-700", Stethoscope],
          ["FIT", kpi.fit, "bg-emerald-100 text-emerald-700", CheckCircle2],
          ["UNFIT", kpi.unfit, "bg-red-100 text-red-700", XCircle],
          ["PASS Alcohol", kpi.pass, "bg-emerald-100 text-emerald-700", CheckCircle2],
          ["FAIL Alcohol", kpi.fail, "bg-red-100 text-red-700", XCircle],
          ["No Check", kpi.nocheck, "bg-slate-100 text-slate-600", Clock],
          ["Total Pilot", kpi.pilot, "bg-sky-100 text-sky-700", Users],
          ["Total FA", kpi.fa, "bg-violet-100 text-violet-700", Users],
        ].map(([l,v,t,I]) => (
          <Card key={l} className="border-slate-200"><CardContent className="p-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${t}`}><I className="w-4 h-4"/></div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
            <div className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{v}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="border-slate-200"><CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <Input placeholder="Cari No ID/Nama/Flight..." value={filters.q} onChange={(e)=>{setFilters({...filters, q:e.target.value}); setPage(1);}} className="col-span-2" data-testid="pf-search"/>
          <Select value={filters.month} onValueChange={(v)=>{setFilters({...filters, month:v}); setPage(1);}}><SelectTrigger data-testid="pf-filter-month"><SelectValue placeholder="Bulan"/></SelectTrigger>
            <SelectContent><SelectItem value="ALL">Semua Bulan</SelectItem>{MONTHS.map(m=><SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.year} onValueChange={(v)=>{setFilters({...filters, year:v}); setPage(1);}}><SelectTrigger data-testid="pf-filter-year"><SelectValue placeholder="Tahun"/></SelectTrigger>
            <SelectContent><SelectItem value="ALL">Semua Tahun</SelectItem>{years.map(y=><SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.base} onValueChange={(v)=>{setFilters({...filters, base:v}); setPage(1);}}><SelectTrigger data-testid="pf-filter-base"><SelectValue placeholder="Base"/></SelectTrigger>
            <SelectContent><SelectItem value="ALL">Semua Base</SelectItem>{bases.map(b=><SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.rank} onValueChange={(v)=>{setFilters({...filters, rank:v}); setPage(1);}}><SelectTrigger data-testid="pf-filter-rank"><SelectValue placeholder="Rank"/></SelectTrigger>
            <SelectContent><SelectItem value="ALL">Semua Rank</SelectItem>{RANKS.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.category} onValueChange={(v)=>{setFilters({...filters, category:v}); setPage(1);}}><SelectTrigger data-testid="pf-filter-category"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent><SelectItem value="ALL">Semua Category</SelectItem>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.note} onValueChange={(v)=>{setFilters({...filters, note:v}); setPage(1);}}><SelectTrigger data-testid="pf-filter-note"><SelectValue placeholder="Note"/></SelectTrigger>
            <SelectContent><SelectItem value="ALL">Semua Note</SelectItem>{NOTES.map(n=><SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
          <Select value={filters.alco} onValueChange={(v)=>{setFilters({...filters, alco:v}); setPage(1);}}><SelectTrigger data-testid="pf-filter-alco"><SelectValue placeholder="Alcohol"/></SelectTrigger>
            <SelectContent><SelectItem value="ALL">Semua Alcohol</SelectItem>{ALCOHOL.map(a=><SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
          <Input placeholder="Cari Remark..." value={filters.remark} onChange={(e)=>{setFilters({...filters, remark:e.target.value}); setPage(1);}} className="col-span-2" data-testid="pf-filter-remark"/>
          <Button variant="outline" onClick={resetFilters} className="col-span-1" data-testid="pf-reset-filter"><RotateCcw className="w-4 h-4 mr-1"/> Reset Filter</Button>
          <div className="col-span-1 flex gap-2">
            <ExportButton rows={filtered} columns={exportCols} title="Pre-Flight Medical Report" filename="preflight-report" testid="pf-export"/>
            {canEdit && <Button onClick={openNew} className="bg-[#00A651] hover:bg-[#008741] flex-1" data-testid="pf-add"><Plus className="w-4 h-4 mr-1"/> Tambah</Button>}
          </div>
        </div>
      </CardContent></Card>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Medical Result · FIT vs UNFIT</CardTitle></CardHeader>
          <CardContent><div className="h-52"><ResponsiveContainer><PieChart><Pie data={chartFit} innerRadius={45} outerRadius={70} dataKey="value">{chartFit.map((_,i)=><Cell key={i} fill={COLORS_FIT[i]}/>)}</Pie><Tooltip/><Legend/></PieChart></ResponsiveContainer></div></CardContent>
        </Card>
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Distribusi Rank</CardTitle></CardHeader>
          <CardContent><div className="h-52"><ResponsiveContainer><BarChart data={chartRank}><XAxis dataKey="name" fontSize={11}/><YAxis fontSize={11}/><Tooltip/><Bar dataKey="count" fill="#00A651" radius={[6,6,0,0]}/></BarChart></ResponsiveContainer></div></CardContent>
        </Card>
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Alcohol Test</CardTitle></CardHeader>
          <CardContent><div className="h-52"><ResponsiveContainer><PieChart><Pie data={chartAlco} innerRadius={45} outerRadius={70} dataKey="value">{chartAlco.map((_,i)=><Cell key={i} fill={COLORS_ALCO[i]}/>)}</Pie><Tooltip/><Legend/></PieChart></ResponsiveContainer></div></CardContent>
        </Card>
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Distribusi Category</CardTitle></CardHeader>
          <CardContent><div className="h-52"><ResponsiveContainer><BarChart data={chartCat}><XAxis dataKey="name" fontSize={9} interval={0} angle={-15} textAnchor="end" height={50}/><YAxis fontSize={11}/><Tooltip/><Bar dataKey="count" radius={[6,6,0,0]}>{chartCat.map((_,i)=><Cell key={i} fill={COLORS_CAT[i%COLORS_CAT.length]}/>)}</Bar></BarChart></ResponsiveContainer></div></CardContent>
        </Card>
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Check per Base</CardTitle></CardHeader>
          <CardContent><div className="h-52"><ResponsiveContainer><BarChart data={chartBase}><XAxis dataKey="name" fontSize={11}/><YAxis fontSize={11}/><Tooltip/><Bar dataKey="count" fill="#FFC72C" radius={[6,6,0,0]}/></BarChart></ResponsiveContainer></div></CardContent>
        </Card>
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Trend Medical Check</CardTitle></CardHeader>
          <CardContent><div className="h-52"><ResponsiveContainer><LineChart data={chartTrend}><CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/><XAxis dataKey="date" fontSize={10}/><YAxis fontSize={11}/><Tooltip/><Line type="monotone" dataKey="count" stroke="#00A651" strokeWidth={2} dot={false}/></LineChart></ResponsiveContainer></div></CardContent>
        </Card>
      </div>

      {/* Report table */}
      <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Detail Laporan ({filtered.length} record)</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
            <Table className="fodm-table"><TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>No ID</TableHead><TableHead>Nama</TableHead><TableHead>Rank</TableHead><TableHead>Flight</TableHead>
                <TableHead>Time</TableHead><TableHead>Alcohol</TableHead><TableHead>Sis</TableHead><TableHead>Dia</TableHead>
                <TableHead>Temp</TableHead><TableHead>Category</TableHead><TableHead>Note</TableHead><TableHead>Remark</TableHead>
                <TableHead>Base</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader><TableBody>
              {isLoading ? <TableRow><TableCell colSpan={14} className="py-6 text-center text-slate-500">Memuat...</TableCell></TableRow>
              : paged.length === 0 ? <TableRow><TableCell colSpan={14} className="py-8 text-center italic text-slate-500">Tidak ada data.</TableCell></TableRow>
              : paged.map(r => (
                <TableRow key={r.id} data-testid="pf-row">
                  <TableCell className="mono text-xs font-semibold">{r.no_id}</TableCell>
                  <TableCell className="font-medium">{r.nama}</TableCell>
                  <TableCell>{rankBadge(r.rank)}</TableCell>
                  <TableCell className="mono text-xs">{r.flight_number}</TableCell>
                  <TableCell className="text-xs">{r.time || "—"}</TableCell>
                  <TableCell>{alcoBadge(r.alcohol_test)}</TableCell>
                  <TableCell className="text-right text-xs">{r.sistole ?? "—"}</TableCell>
                  <TableCell className="text-right text-xs">{r.diastole ?? "—"}</TableCell>
                  <TableCell className="text-right text-xs">{r.temperature ?? "—"}</TableCell>
                  <TableCell>{catBadge(r.category)}</TableCell>
                  <TableCell>{noteBadge(r.note)}</TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate" title={r.remark}>{r.remark || "—"}</TableCell>
                  <TableCell className="font-medium text-xs">{r.base}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={()=>setViewing(r)} data-testid="pf-view"><Eye className="w-4 h-4"/></Button>
                      {canEdit && <>
                        <Button size="sm" variant="ghost" onClick={()=>openEdit(r)} data-testid="pf-edit"><Pencil className="w-4 h-4"/></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600"><Trash2 className="w-4 h-4"/></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Hapus laporan?</AlertDialogTitle><AlertDialogDescription>Data medical <b>{r.nama}</b> ({r.no_id}) akan dihapus.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={()=>mDel.mutate(r.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody></Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-500 mt-3">
              <div>Menampilkan {(page-1)*PER + 1}–{Math.min(page*PER, filtered.length)} dari {filtered.length}</div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Sebelumnya</Button>
                <Button size="sm" variant="outline" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Berikutnya</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ReportFormDialog open={openForm} onOpenChange={setOpenForm} initial={editing} onSave={(b)=>mSave.mutate(b)} />

      <Dialog open={!!viewing} onOpenChange={(o)=>!o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail Pre-Flight Medical Report</DialogTitle></DialogHeader>
          {viewing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["No ID", viewing.no_id],["Nama", viewing.nama],["Rank", viewing.rank],
                ["Flight Number", viewing.flight_number],["Tanggal", viewing.date],["Time", viewing.time],
                ["Alcohol Test", viewing.alcohol_test],["Base", viewing.base],
                ["Sistole", viewing.sistole],["Diastole", viewing.diastole],
                ["Temperature", viewing.temperature],["Category", viewing.category],
                ["Note", viewing.note],["Remark", viewing.remark || "—"],
              ].map(([l,v]) => (
                <div key={l} className="p-2 rounded border border-slate-100">
                  <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{l}</div>
                  <div className="mt-0.5 font-medium text-slate-800">{v ?? "—"}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
